import { useState, useEffect } from "react";
import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { supabase } from "../../../lib/supabase";
import { PickupRequest, BookingSupplier } from "../PricingCalculatorTypes";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";

export const usePickupRequest = (transportType: string, incoterm: string) => {
  const { t } = useTranslation();
  const { state, dispatch } = useBookingWizard();

  const [isPickupRequestModalVisible, setIsPickupRequestModalVisible] = useState(false);
  const [isRequestingPickup, setIsRequestingPickup] = useState(false);
  const [pickupRequest, setPickupRequest] = useState<PickupRequest | null>(null);
  const [pickupRequestHistory, setPickupRequestHistory] = useState<PickupRequest[]>([]);
  const [isPickupDetailsModalVisible, setIsPickupDetailsModalVisible] = useState(false);
  const [isHistoryDetailsModalVisible, setIsHistoryDetailsModalVisible] = useState(false);
  const [selectedHistoryRequest, setSelectedHistoryRequest] = useState<PickupRequest | null>(null);
  const [bookingSupplier, setBookingSupplier] = useState<BookingSupplier | null>(null);

  // Fetch the booking supplier if we have a supplier_id
  useEffect(() => {
    if (state.supplier_id) {
      const fetchBookingSupplier = async () => {
        try {
          const { data, error } = await supabase
            .from("booking_supplier")
            .select("*")
            .eq("id", state.supplier_id)
            .single();

          if (error) {
            console.error("Error fetching booking supplier:", error);
            return;
          }

          if (data) {
            setBookingSupplier(data);
          }
        } catch (error) {
          console.error("Error in booking supplier fetch:", error);
        }
      };

      fetchBookingSupplier();
    } else {
      setBookingSupplier(null);
    }
  }, [state.supplier_id]);

  // Fetch latest pickup request for this booking
  useEffect(() => {
    if (state.id) {
      fetchLatestPickupRequest();
    }
  }, [state.id]);

  const fetchLatestPickupRequest = async () => {
    if (!state.id) return;

    try {
      const { data, error } = await supabase
        .from("booking_pickup_request")
        .select("*")
        .eq("booking_id", state.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("Error fetching pickup request:", error);
        return;
      }

      if (data) {
        setPickupRequest(data);
        if (data.status === "accepted" && data.cost) {
          dispatch({
            type: "UPDATE_STEP",
            payload: {
              step: state.currentStep,
              data: {
                pickupFee: data.cost,
              },
            },
          });
        }
      }
    } catch (error) {
      console.error("Error in pickup request fetch:", error);
    }
  };

  const fetchAllPickupRequests = async () => {
    if (!state.id) return;

    try {
      const { data, error } = await supabase
        .from("booking_pickup_request")
        .select("*")
        .eq("booking_id", state.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching pickup request history:", error);
        return;
      }

      if (data && data.length > 0) {
        setPickupRequestHistory(data);
      } else {
        setPickupRequestHistory([]);
      }
    } catch (error) {
      console.error("Error in pickup request history fetch:", error);
    }
  };

  const showPickupDetails = () => {
    setIsPickupDetailsModalVisible(true);
    fetchAllPickupRequests();
  };

  const requestNewPickupFeeQuote = async () => {
    Modal.confirm({
      title: t("newQuoteConfirmTitle", "Request New Pickup Fee Quote?"),
      content: t(
        "newQuoteConfirmContent",
        "Requesting a new quote will expire all previous offers. You will need to wait for the new quote to be processed before you can confirm it."
      ),
      okText: t("requestNewQuote", "Request New Quote"),
      cancelText: t("cancel", "Cancel"),
      onOk: async () => {
        if (pickupRequest && (pickupRequest.status === "offer" || pickupRequest.status === "pending")) {
          try {
            const { error } = await supabase
              .from("booking_pickup_request")
              .update({ status: "expired" })
              .eq("booking_id", state.id)
              .in("status", ["offer", "pending"]);

            if (error) {
              console.error("Error expiring old pickup requests:", error);
              message.error(t("expireRequestError", "Failed to expire previous requests"));
            } else {
              message.success(t("expireRequestSuccess", "Previous offers have been expired"));
            }
          } catch (error) {
            console.error("Error in expiring old pickup requests:", error);
          }
        }

        setIsPickupDetailsModalVisible(false);
        setTimeout(() => {
          openPickupRequestModal();
        }, 300);
      },
    });
  };

  const openPickupRequestModal = () => {
    if (!state.id) {
      message.error(
        t(
          "pickupRequestNeedsBooking",
          "You need to save the booking first before requesting a pickup fee quote"
        )
      );
      return;
    }
    setIsPickupRequestModalVisible(true);
  };

  const acceptPickupFeeOffer = async () => {
    if (!pickupRequest || pickupRequest.status !== "offer" || !pickupRequest.cost) {
      return;
    }

    try {
      const { error: updateError } = await supabase
        .from("booking_pickup_request")
        .update({
          status: "accepted",
          accepted_at: new Date().toISOString(),
        })
        .eq("id", pickupRequest.id);

      if (updateError) throw updateError;

      dispatch({
        type: "UPDATE_STEP",
        payload: {
          step: state.currentStep,
          data: {
            pickupFee: pickupRequest.cost,
          },
        },
      });

      setPickupRequest({
        ...pickupRequest,
        status: "accepted",
        accepted_at: new Date().toISOString(),
      });

      message.success(t("pickupOfferAccepted", "Pickup fee offer accepted"));
    } catch (error) {
      console.error("Error accepting pickup fee offer:", error);
      message.error(t("pickupOfferAcceptError", "Failed to accept pickup fee offer"));
    }
  };

  const prepareBookingBlob = () => {
    const totalCBM = state.products?.reduce((sum, product) => sum + (product.cbm || 0), 0) || 0;
    const totalCartons = state.products?.reduce((sum, product) => sum + (product.cartons || 0), 0) || 0;
    const totalWeight = state.products?.reduce((sum, product) => sum + (product.gross_weight || 0), 0) || 0;
    const totalPieces = state.products?.reduce((sum, product) => sum + (product.pieces || 0), 0) || 0;

    return {
      booking_id: state.id,
      reference: state.reference,
      supplier: bookingSupplier
        ? {
            name: bookingSupplier.name,
            address: bookingSupplier.address,
            city: bookingSupplier.city,
            country: bookingSupplier.country,
            province: bookingSupplier.province,
            phone: bookingSupplier.phone,
            email: bookingSupplier.email,
          }
        : null,
      products_summary: {
        total_cbm: totalCBM,
        total_cartons: totalCartons,
        total_weight: totalWeight,
        total_pieces: totalPieces,
      },
      transport_info: {
        transport_type: transportType,
        incoterm: incoterm,
        origin_pickup_date: state.origin_pickup_date,
      },
    };
  };

  const handlePickupFeeRequest = async () => {
    try {
      setIsRequestingPickup(true);

      const bookingBlob = prepareBookingBlob();

      const { error } = await supabase
        .from("booking_pickup_request")
        .insert({
          booking_id: state.id,
          status: "requested",
          booking_blob: bookingBlob,
          notes: null,
          cost: null,
        })
        .select()
        .single();

      if (error) throw error;

      message.success(t("pickupRequestSuccess", "Pickup fee request submitted successfully"));
      setIsPickupRequestModalVisible(false);

      fetchLatestPickupRequest();
    } catch (error) {
      console.error("Error submitting pickup request:", error);
      message.error(t("pickupRequestError", "Failed to submit pickup fee request"));
    } finally {
      setIsRequestingPickup(false);
    }
  };

  const showHistoryRequestDetails = (request: PickupRequest) => {
    setSelectedHistoryRequest(request);
    setIsHistoryDetailsModalVisible(true);
  };

  return {
    isPickupRequestModalVisible,
    setIsPickupRequestModalVisible,
    isRequestingPickup,
    pickupRequest,
    pickupRequestHistory,
    isPickupDetailsModalVisible,
    setIsPickupDetailsModalVisible,
    isHistoryDetailsModalVisible,
    setIsHistoryDetailsModalVisible,
    selectedHistoryRequest,
    bookingSupplier,
    showPickupDetails,
    requestNewPickupFeeQuote,
    openPickupRequestModal,
    acceptPickupFeeOffer,
    handlePickupFeeRequest,
    showHistoryRequestDetails,
  };
};
















